<template>
  <div class="auto-finetune-schedule">
    <div
      v-if="isLoadingMetadata"
      class="d-flex justify-content-center align-items-center pt-35-px"
    >
      <Spinner />
    </div>

    <div v-else-if="metadataError">
      <a-alert
        type="error"
        show-icon
        class="mb-3"
        message="Auto-finetune settings could not be loaded"
        :description="metadataError"
      />
      <a-button type="primary" @click="loadMetadata">
        Retry
      </a-button>
    </div>

    <template v-else-if="metadata">
      <template v-if="isOnce">
        <a-form layout="vertical" @submit.prevent="onEnqueueOnce">
          <p class="text-muted mb-3">
            The inclusive window cannot exceed {{ maximumLookbackLabel }}.
          </p>

          <a-row :gutter="24" class="analysis-window">
            <a-col :xs="24" :md="12">
              <a-form-item label="Start datetime">
                <a-date-picker
                  v-model="onceStartDateTime"
                  :showTime="{ format: 'HH:mm:ss' }"
                  format="YYYY-MM-DD HH:mm:ss"
                  class="full-w"
                  :disabledDate="disabledOnceDate"
                  placeholder="Select start datetime"
                />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :md="12">
              <a-form-item label="End datetime">
                <a-date-picker
                  v-model="onceEndDateTime"
                  :showTime="{ format: 'HH:mm:ss' }"
                  format="YYYY-MM-DD HH:mm:ss"
                  class="full-w"
                  :disabledDate="disabledOnceDate"
                  placeholder="Select end datetime"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <a-alert
            v-if="onceAnalysisWindowError"
            type="error"
            show-icon
            class="analysis-window-error mb-3"
            :message="onceAnalysisWindowError"
          />

          <AutoFinetuneThresholdFields
            :metadata="metadata"
            :threshold-alert="onceThresholdAlert"
            :frequency-threshold="onceFrequencyThreshold"
            @update:threshold-alert="onceThresholdAlert = $event"
            @update:frequency-threshold="onceFrequencyThreshold = $event"
          />

          <AutoFinetuneContactGroupField
            v-model="onceContactGroupId"
            :contact-groups="contactGroups"
            :loading="isLoadingContactGroups"
          />

          <a-button
            type="primary"
            size="large"
            html-type="submit"
            :loading="isEnqueueingOnce"
            :disabled="!canEnqueueOnce"
          >
            Enqueue Suggest
          </a-button>
        </a-form>
      </template>

      <template v-else>
        <a-collapse v-model="activePanel" class="mb-20-px">
          <a-collapse-panel key="create" header="Create New Schedule">
            <AutoFinetuneRecurringScheduleForm
              v-if="createDraft"
              v-model="createDraft"
              :metadata="metadata"
              :contact-groups="contactGroups"
              :loading-contact-groups="isLoadingContactGroups"
              mode="create"
              :saving="isCreating"
              @submit="saveSchedule('create', $event)"
            />
          </a-collapse-panel>
        </a-collapse>

        <h3 class="section-title mb-3">
          Active Schedules
        </h3>

        <div
          v-if="isLoadingSchedules"
          class="d-flex justify-content-center align-items-center pt-35-px"
        >
          <Spinner />
        </div>

        <transition name="slide-up">
          <div v-if="!isLoadingSchedules">
            <a-table
              v-if="schedules.length > 0"
              :columns="scheduleColumns"
              :dataSource="pagedSchedules"
              :rowKey="record => record.id"
              :pagination="false"
              :scroll="{ x: 1620 }"
              size="middle"
            >
              <template slot="frequency" slot-scope="text">
                <a-tag>
                  {{ text }}
                </a-tag>
              </template>
              <template slot="schedule" slot-scope="text, record">
                {{ formatRecurringSchedule(record) }}
              </template>
              <template slot="lookback" slot-scope="text, record">
                {{ formatScheduleLookback(record) }}
              </template>
              <template slot="frequency_threshold" slot-scope="text">
                {{ formatPercentage(text) }}
              </template>
              <template slot="contact_group_id" slot-scope="text">
                {{ contactGroupName(text) }}
              </template>
              <template slot="next_run_at" slot-scope="text">
                {{ formatDateTime(text) }}
              </template>
              <template slot="last_run_at" slot-scope="text">
                {{ formatDateTime(text) }}
              </template>
              <template slot="analysis_window" slot-scope="text, record">
                <template v-if="record.start_datetime && record.end_datetime">
                  <div>{{ formatDateTime(record.start_datetime) }}</div>
                  <div>{{ formatDateTime(record.end_datetime) }}</div>
                </template>
                <template v-else>
                  —
                </template>
              </template>
              <template slot="action" slot-scope="text, record">
                <a-button
                  type="primary"
                  size="small"
                  ghost
                  class="mr-1"
                  @click="openEditSchedule(record)"
                >
                  <a-icon type="edit" />
                </a-button>
                <a-popconfirm
                  title="Are you sure you want to delete this schedule?"
                  ok-text="Yes"
                  cancel-text="No"
                  @confirm="onDeleteSchedule(record.id)"
                >
                  <a-button
                    type="danger"
                    size="small"
                    ghost
                    :disabled="deletingScheduleId === record.id"
                  >
                    <a-icon type="delete" />
                  </a-button>
                </a-popconfirm>
              </template>
            </a-table>
            <a-empty v-else description="No active schedules" />
            <div v-if="schedules.length > 0" class="a-footer mt-3">
              <PaginationBar
                :current="pagin.current"
                :pageSize.sync="pagin.pageSize"
                :total="pagin.total"
                @change="paginChange"
              />
            </div>
          </div>
        </transition>

        <a-modal
          :visible="editModalVisible"
          :width="880"
          :destroy-on-close="true"
          title="Edit schedule"
          :footer="null"
          @cancel="closeEditModal"
        >
          <AutoFinetuneRecurringScheduleForm
            v-if="editDraft"
            v-model="editDraft"
            :metadata="metadata"
            :contact-groups="contactGroups"
            :loading-contact-groups="isLoadingContactGroups"
            mode="edit"
            :saving="isUpdating"
            @cancel="closeEditModal"
            @submit="saveSchedule('edit', $event)"
          />
        </a-modal>
      </template>
    </template>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import ContactGroupService from '@modules/ContactGroup/services/contact-group.service';
import FinetuneSuggestionService from '@modules/Policy/services/FinetuneSuggestion.service';
import { getApiErrorMessage } from '@shared/utils/finetune';
import { fetchAutoFinetuneMetadata } from '../helpers/metadata';
import {
  createPreviousAccountDayWindow,
  formatAccountDateTime,
  pickerDateTimeInAccountTimezone,
  resolveAccountTimezone
} from '../helpers/dateTime';
import {
  formatPercentage,
  percentageToRatio,
  ratioToPercentage
} from '../helpers/percentage';
import {
  buildRecurringSchedulePayload,
  createDefaultScheduleFormState,
  formatRecurringSchedule,
  formatScheduleLookback,
  mapRecurringScheduleRecord,
  validateAutoFinetuneThresholds,
  validateRecurringScheduleDraft
} from '../helpers/recurringSchedule';
import { AUTO_FINETUNE_ROUTE_NAMES } from '../router/routes';
import AutoFinetuneContactGroupField from './AutoFinetuneContactGroupField.vue';
import AutoFinetuneRecurringScheduleForm from './AutoFinetuneRecurringScheduleForm.vue';
import AutoFinetuneThresholdFields from './AutoFinetuneThresholdFields.vue';

export default {
  name: 'AutoFinetuneSchedule',
  components: {
    AutoFinetuneContactGroupField,
    AutoFinetuneRecurringScheduleForm,
    AutoFinetuneThresholdFields
  },
  props: {
    mode: {
      type: String,
      default: 'recurring',
      validator: (value) => ['once', 'recurring'].includes(value)
    }
  },
  data() {
    return {
      metadata: null,
      isLoadingMetadata: true,
      metadataError: '',
      onceStartDateTime: null,
      onceEndDateTime: null,
      onceThresholdAlert: null,
      onceFrequencyThreshold: null,
      onceContactGroupId: null,
      isEnqueueingOnce: false,
      contactGroups: [],
      isLoadingContactGroups: true,
      activePanel: ['create'],
      createDraft: null,
      isCreating: false,
      editModalVisible: false,
      editingScheduleId: null,
      editDraft: null,
      isUpdating: false,
      deletingScheduleId: null,
      schedules: [],
      isLoadingSchedules: true,
      pagin: {
        current: 1,
        pageSize: 10,
        total: 0
      },
      scheduleColumns: [
        {
          title: '#',
          dataIndex: '__rowNumber',
          width: 60
        },
        {
          title: 'Frequency',
          dataIndex: 'frequency',
          scopedSlots: { customRender: 'frequency' },
          width: 100
        },
        {
          title: 'Schedule',
          dataIndex: 'schedule',
          scopedSlots: { customRender: 'schedule' },
          width: 210
        },
        {
          title: 'Lookback',
          dataIndex: 'lookback_amount',
          scopedSlots: { customRender: 'lookback' },
          width: 120
        },
        {
          title: 'Alert Threshold',
          dataIndex: 'threshold_alert',
          width: 130
        },
        {
          title: 'Frequency threshold',
          dataIndex: 'frequency_threshold',
          scopedSlots: { customRender: 'frequency_threshold' },
          width: 150
        },
        {
          title: 'Contact Group',
          dataIndex: 'contact_group_id',
          scopedSlots: { customRender: 'contact_group_id' },
          width: 160
        },
        {
          title: 'Next Run',
          dataIndex: 'next_run_at',
          scopedSlots: { customRender: 'next_run_at' },
          width: 180
        },
        {
          title: 'Last Run',
          dataIndex: 'last_run_at',
          scopedSlots: { customRender: 'last_run_at' },
          width: 180
        },
        {
          title: 'Next Analysis Window',
          dataIndex: 'start_datetime',
          scopedSlots: { customRender: 'analysis_window' },
          width: 200
        },
        {
          title: '',
          dataIndex: 'action',
          scopedSlots: { customRender: 'action' },
          width: 80
        }
      ]
    };
  },
  computed: {
    isOnce() {
      return this.mode === 'once';
    },
    customerTimezone() {
      return resolveAccountTimezone(this.$store);
    },
    maximumLookbackSeconds() {
      return Number(
        this.metadata &&
          this.metadata.limits &&
          this.metadata.limits.max_lookback_seconds
      );
    },
    maximumLookbackLabel() {
      const seconds = this.maximumLookbackSeconds;
      if (!Number.isFinite(seconds)) return 'the configured limit';
      const days = seconds / (24 * 60 * 60);
      if (Number.isInteger(days)) {
        return `${days} day${days === 1 ? '' : 's'}`;
      }
      const hours = seconds / (60 * 60);
      return `${hours} hour${hours === 1 ? '' : 's'}`;
    },
    onceAnalysisWindow() {
      if (!this.onceStartDateTime || !this.onceEndDateTime) return null;
      return {
        start: pickerDateTimeInAccountTimezone(
          this.onceStartDateTime,
          this.customerTimezone
        ),
        end: pickerDateTimeInAccountTimezone(
          this.onceEndDateTime,
          this.customerTimezone
        )
      };
    },
    onceAnalysisWindowError() {
      if (!this.onceStartDateTime || !this.onceEndDateTime) {
        return 'Select both a start and an end datetime.';
      }
      if (
        !this.onceAnalysisWindow ||
        !this.onceAnalysisWindow.start.isValid() ||
        !this.onceAnalysisWindow.end.isValid()
      ) {
        return 'Enter a valid start and end datetime.';
      }

      const spanSeconds = this.onceAnalysisWindow.end.diff(
        this.onceAnalysisWindow.start,
        'seconds',
        true
      );
      if (spanSeconds < 0) {
        return 'End datetime must be greater than or equal to start datetime.';
      }
      if (
        Number.isFinite(this.maximumLookbackSeconds) &&
        spanSeconds > this.maximumLookbackSeconds
      ) {
        return `Analysis window cannot exceed ${this.maximumLookbackLabel}.`;
      }
      if (this.onceAnalysisWindow.end.isAfter(moment.tz(this.customerTimezone))) {
        return 'End datetime cannot be in the future.';
      }
      return validateAutoFinetuneThresholds(
        {
          thresholdAlert: this.onceThresholdAlert,
          frequencyThreshold: this.onceFrequencyThreshold
        },
        this.metadata
      );
    },
    canEnqueueOnce() {
      return (
        !!this.metadata &&
        !this.isEnqueueingOnce &&
        !this.onceAnalysisWindowError
      );
    },
    pagedSchedules() {
      const pageSize = Math.max(1, Number(this.pagin.pageSize) || 10);
      const current = Math.max(1, Number(this.pagin.current) || 1);
      const start = (current - 1) * pageSize;
      return this.schedules.slice(start, start + pageSize).map((record, index) => ({
        ...record,
        __rowNumber: start + index + 1
      }));
    }
  },
  mounted() {
    this.loadMetadata();
    this.loadContactGroups();
    if (!this.isOnce) this.fetchSchedules();
  },
  methods: {
    async loadMetadata() {
      this.isLoadingMetadata = true;
      this.metadataError = '';
      try {
        this.metadata = await fetchAutoFinetuneMetadata();
        if (this.isOnce) {
          this.onceThresholdAlert = Number(
            this.metadata.defaults.threshold_alert
          );
          this.onceFrequencyThreshold = ratioToPercentage(
            this.metadata.defaults.frequency_threshold
          );
          this.resetOnceWindow();
        } else if (!this.createDraft) {
          this.createDraft = createDefaultScheduleFormState(this.metadata);
        }
      } catch (error) {
        this.metadata = null;
        this.metadataError = getApiErrorMessage(
          error,
          'Failed to load auto-finetune settings.'
        );
      } finally {
        this.isLoadingMetadata = false;
      }
    },
    async loadContactGroups() {
      this.isLoadingContactGroups = true;
      try {
        const response = await ContactGroupService.getList();
        const groups = Array.isArray(response)
          ? response
          : (response && response.data) || [];
        this.contactGroups = groups.map((group) => ({
          label: group.description,
          value: group.id
        }));
      } catch (error) {
        this.$message.error(
          getApiErrorMessage(error, 'Failed to load contact groups.')
        );
      } finally {
        this.isLoadingContactGroups = false;
      }
    },
    resetOnceWindow() {
      const window = createPreviousAccountDayWindow(this.customerTimezone);
      this.onceStartDateTime = window.start;
      this.onceEndDateTime = window.end;
    },
    disabledOnceDate(current) {
      if (!current) return false;
      const currentDate = moment.tz(
        moment(current).format('YYYY-MM-DD'),
        'YYYY-MM-DD',
        this.customerTimezone
      );
      return currentDate.isAfter(moment.tz(this.customerTimezone).endOf('day'));
    },
    async onEnqueueOnce() {
      if (this.onceAnalysisWindowError || !this.onceAnalysisWindow) {
        this.$message.error(
          this.onceAnalysisWindowError || 'Select a valid analysis window.'
        );
        return;
      }

      this.isEnqueueingOnce = true;
      try {
        const response = await FinetuneSuggestionService.enqueueSuggest({
          start_datetime: this.onceAnalysisWindow.start.format(),
          end_datetime: this.onceAnalysisWindow.end.format(),
          threshold_alert: Number(this.onceThresholdAlert),
          frequency_threshold: percentageToRatio(this.onceFrequencyThreshold),
          contact_group_id:
            this.onceContactGroupId == null
              ? null
              : Number(this.onceContactGroupId)
        });
        const jobId =
          response &&
          ((response.job && response.job.id) ||
            (response.payload && response.payload.job_id) ||
            response.job_id);
        if (jobId) {
          const detailRoute = {
            name: AUTO_FINETUNE_ROUTE_NAMES.SUGGESTION_DETAIL,
            query: { job_id: jobId }
          };
          let closeMessage = () => {};
          closeMessage = this.$message.success(
            this.$createElement('span', [
              'Job queued; results may take a few minutes. ',
              this.$createElement(
                'a',
                {
                  attrs: { href: this.$router.resolve(detailRoute).href },
                  on: {
                    click: (event) => {
                      event.preventDefault();
                      closeMessage();
                      this.$router.push(detailRoute);
                    }
                  }
                },
                'View details'
              )
            ]),
            3
          );
        } else {
          this.$message.success('Job queued; results may take a few minutes.');
        }
      } catch (error) {
        this.$message.error(
          getApiErrorMessage(error, 'Failed to enqueue job. Please try again.')
        );
      } finally {
        this.isEnqueueingOnce = false;
      }
    },
    async fetchSchedules() {
      try {
        const response = await FinetuneSuggestionService.fetchSchedules();
        this.schedules = Array.isArray(response)
          ? response
          : response && Array.isArray(response.data)
            ? response.data
            : [];
        this.pagin.total = this.schedules.length;
        const lastPage = Math.max(
          1,
          Math.ceil(this.pagin.total / this.pagin.pageSize)
        );
        if (this.pagin.current > lastPage) this.pagin.current = lastPage;
      } catch (error) {
        this.$message.error(
          getApiErrorMessage(error, 'Failed to load schedules.')
        );
      } finally {
        this.isLoadingSchedules = false;
      }
    },
    paginChange(pagin) {
      this.pagin = {
        ...this.pagin,
        current: Math.max(1, Number(pagin.current) || 1),
        pageSize: Math.max(1, Number(pagin.pageSize) || 10)
      };
    },
    async saveSchedule(mode, draft) {
      if (
        !this.metadata ||
        (mode === 'create' && this.isCreating) ||
        (mode === 'edit' && (this.isUpdating || !this.editingScheduleId))
      ) {
        return;
      }
      const validationError = validateRecurringScheduleDraft(
        draft,
        this.metadata
      );
      if (validationError) {
        this.$message.error(validationError);
        return;
      }

      if (mode === 'create') this.isCreating = true;
      else this.isUpdating = true;

      try {
        const payload = buildRecurringSchedulePayload(draft);
        if (mode === 'create') {
          await FinetuneSuggestionService.createSchedule(payload);
          this.$message.success('Schedule created successfully.');
          this.createDraft = createDefaultScheduleFormState(this.metadata);
        } else {
          await FinetuneSuggestionService.updateSchedule(
            this.editingScheduleId,
            payload
          );
          this.$message.success('Schedule updated successfully.');
          this.closeEditModal();
        }
        await this.fetchSchedules();
      } catch (error) {
        this.$message.error(
          getApiErrorMessage(
            error,
            mode === 'create'
              ? 'Failed to create schedule.'
              : 'Failed to update schedule.'
          )
        );
      } finally {
        if (mode === 'create') this.isCreating = false;
        else this.isUpdating = false;
      }
    },
    openEditSchedule(record) {
      this.editingScheduleId = record.id;
      this.editDraft = mapRecurringScheduleRecord(record, this.metadata);
      this.editModalVisible = true;
    },
    closeEditModal() {
      this.editModalVisible = false;
      this.editingScheduleId = null;
      this.editDraft = null;
    },
    async onDeleteSchedule(id) {
      if (this.deletingScheduleId) return;
      this.deletingScheduleId = id;
      try {
        await FinetuneSuggestionService.deleteSchedule(id);
        this.$message.success('Schedule deleted.');
        await this.fetchSchedules();
      } catch (error) {
        this.$message.error(
          getApiErrorMessage(error, 'Failed to delete schedule.')
        );
      } finally {
        this.deletingScheduleId = null;
      }
    },
    formatRecurringSchedule,
    formatScheduleLookback,
    formatPercentage,
    contactGroupName(id) {
      if (id == null) return 'No email notifications';
      const group = this.contactGroups.find(
        (option) => String(option.value) === String(id)
      );
      return group ? group.label : `Contact group #${id}`;
    },
    formatDateTime(value) {
      return formatAccountDateTime(value, this.customerTimezone);
    }
  }
};
</script>

<style lang="scss" scoped>
.auto-finetune-schedule {
  width: 100%;
}

.full-w {
  width: 100%;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
}
</style>
